import { useEffect, useRef, useState } from "react";
import { UseEntityStore, createEntityStore } from "../../common/entityStore";
import { ArticleCategoryDto, ProductDto, TmOrderDto } from "./common";
import { propNumberField } from "../../common/entityStoreForm";
import { IconButton } from "@mui/material";
import { RemoveCircle } from "@mui/icons-material";
import { propInt, propMaxValue, propMinValue, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { useRxJsSub } from "../../common/utils";
import { BehaviorSubject, distinctUntilChanged, map } from "rxjs";
import { UseListStore } from "../../common/listStore";
import { formatNumber, getResource, getValidationText } from "../../common/localization";
import { useDefaultValues } from "../../common/defaultValues";
import { ShipmentDetailDto, getOrderCalculation } from "../../common/order";
import { ProjectMailOrderConditionDto } from "../../common/types";
import { useAccountStore } from "../../common/account";

function getText(key: string, defaultMessage?: string) {
    return getResource("Ui.Order." + key, defaultMessage) as string;
}

export function DetailQuantity({
    detail,
    useStore,
    ask,
    useMoCondsStore,
    updateOrder,
    product$,
    useArticleCategoriesStore,
}: {
    detail: ShipmentDetailDto;
    useStore: UseEntityStore<TmOrderDto>;
    ask(title: string, message: string | JSX.Element): Promise<boolean>;
    useMoCondsStore: UseListStore<ProjectMailOrderConditionDto>;
    updateOrder(): void;
    product$: BehaviorSubject<ProductDto | undefined>;
    useArticleCategoriesStore: UseListStore<ArticleCategoryDto>;
}) {
    function isQtyValid(p: number) {
        return p % detail.baseQuantity === 0;
    }

    const [useColumnStore] = useState(() => createEntityStore<ArticleAddData>({
        data: {
            quantity: detail.quantity,
        },
        id: null,
        propValidators: [
            propValidValue("quantity", "quantityFormatted"),
            propRequired("quantity"),
            propInt("quantity"),
            propMinValue("quantity", 1),
            propMaxValue("quantity", 100),
            propValueValidator("quantity", p => p != null && !isQtyValid(p) &&
                getValidationText("QuantityMustBeMultiple")
                    .replaceAll("{0}", formatNumber(detail.baseQuantity, useDefaultValues.getState().clientData.landCode, false))),
        ],
        isValidationEnabled: true,
    }));
    const { isValid } = useColumnStore(p => ({ isValid: Object.keys(p.propValidationMessages).length === 0 }));
    const lastIsValid = useRef(true);
    useEffect(() => {
        if (isValid === lastIsValid.current) {
            return;
        }
        useStore.getState().setClientDataProp("detailsErrors", useStore.getState().clientData.detailsErrors + (isValid ? -1 : 1));
        lastIsValid.current = isValid;
        return () => {
            if (isValid === lastIsValid.current) {
                return;
            }
            useStore.getState().setClientDataProp("detailsErrors", useStore.getState().clientData.detailsErrors + (isValid ? -1 : 1));
            lastIsValid.current = isValid;
        };
    }, [isValid, useStore]);
    useRxJsSub(() => useStore.getState().clientData$.pipe(map(p => (p.data.details ?? [])
        .find(p => p.saleArticleId === detail.saleArticleId)?.quantity), distinctUntilChanged()).subscribe(p => {
            if (p != null) {
                useColumnStore.getState().setClientDataProp("quantity", p);
            }
        }));
    async function confirmChange(qty: number | null) {
        if (qty == null || !isQtyValid(qty)) {
            return true;
        }
        const order = useStore.getState().clientData;
        const moCondsStore = useMoCondsStore.getState();
        const details = (order.details ?? []).map<ShipmentDetailDto>(p => p.saleArticleId === detail.saleArticleId ? { ...p, quantity: qty } : p);
        const salesArticles = useArticleCategoriesStore.getState().items.flatMap(p => p.articles);
        const info = getOrderCalculation({ ...order, details }, { isLoaded: moCondsStore.fetched && !moCondsStore.fetching, items: moCondsStore.items }, product$.value?.maxDiscountAmount, salesArticles, null, useAccountStore.getState().data?.accountData.accountInfo.callCenterId, order.langPref);
        if (info.gifts.giftsRemoved) {
            return await ask(getText("RemoveGift.Title"), <>
                {getText("RemoveGift.Content")}
                <ul>{info.gifts.removedGifts.map(p => <li key={p.saleArticleId}>{p.name}</li>)}</ul>
            </>);
        }
        return true;
    }
    async function remove() {
        if (!await confirmChange(0)) {
            return;
        }
        const details = (useStore.getState().clientData.details?.slice() ?? [])
            .filter(p => p.saleArticleId !== detail.saleArticleId);
        useStore.getState().setClientDataProp("details", details);
        useStore.getState().setClientDataProp("giftsNotSelectedOk", undefined);
        updateOrder();
    }
    async function save() {
        const quantity = useColumnStore.getState().clientData.quantity!;
        if (quantity > 9 && quantity > detail.quantity && !await ask(getText("ChangeArticle.Title"), <>{getText("ChangeArticle.Content").replaceAll("{0}", formatNumber(quantity, useDefaultValues.getState().clientData.landCode, false))}</>)) {
            useColumnStore.getState().setClientDataProp("quantity", detail.quantity);
            return;
        }
        if (!await confirmChange(useColumnStore.getState().clientData.quantity)) {
            useColumnStore.getState().setClientDataProp("quantity", detail.quantity);
            return;
        }
        const details = (useStore.getState().clientData.details?.slice() ?? [])
            .map(p => p.saleArticleId === detail.saleArticleId ? { ...p, quantity } : p);
        useStore.getState().setClientDataProp("details", details);
        useStore.getState().setClientDataProp("giftsNotSelectedOk", undefined);
        updateOrder();
    }
    return <>
        <IconButton sx={{ ml: -2 }} onClick={remove}><RemoveCircle /></IconButton>
        {propNumberField(useColumnStore)("quantity", "quantityFormatted", "", {
            showTooltipError: true, onBlur: () => {
                useColumnStore.getState().setClientDataProp("quantityFormatted", undefined);
                if (useColumnStore.getState().isSaveDisabled) {
                    useColumnStore.getState().setClientDataProp("quantity", detail.quantity);
                    return;
                }
                save();
            }, sx: { "& input": { padding: "3px 6px" } },
        })}
    </>;
}

interface ArticleAddData {
    quantity: number | null;
    quantityFormatted?: string;
}
